import React, { useState, useCallback } from "react"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useSnackbar } from "notistack"
import { verifyResponse, httpPut } from "utils/httpRequest"
import { generateMcpPassword } from "utils/generateMcpPassword"

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback

const McpResetPasswordDialog = ({ onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const [user, setUser] = useState(null)
  const [generatedPassword, setGeneratedPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const openDialog = useCallback((mcpUser) => {
    setUser(mcpUser)
    setGeneratedPassword(generateMcpPassword())
    setError(null)
    setOpen(true)
  }, [])

  const closeDialog = useCallback(() => {
    setOpen(false)
    setUser(null)
    setGeneratedPassword("")
    setError(null)
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword)
      enqueueSnackbar("Password copied", { variant: "success" })
    } catch (copyError) {
      console.error("Failed to copy password:", copyError)
      enqueueSnackbar("Failed to copy password", { variant: "error" })
    }
  }

  const handleConfirm = async () => {
    if (!user?.mcpUserId || !generatedPassword) {
      return
    }

    setError(null)
    setLoading(true)
    try {
      const response = await httpPut(
        `admin/mcp-users/${encodeURIComponent(user.mcpUserId)}`,
        { password: generatedPassword }
      )
      verifyResponse(response.data)
      enqueueSnackbar("Password updated successfully", { variant: "success" })
      closeDialog()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, "Failed to reset password")
      setError(message)
      enqueueSnackbar(message, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  if (!open) {
    return [openDialog, null, false, closeDialog]
  }

  const dialogContent = (
    <div>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography
        variant="body2"
        sx={{ color: "var(--cms-text-secondary, #64748b)", mb: 2 }}
      >
        Reset password for{" "}
        <strong>{user?.mcpUserEmail || "this user"}</strong>. Copy the new
        password below, then confirm to save it.
      </Typography>

      <div className="form-group">
        <label>New Password</label>
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <TextField
            fullWidth
            value={generatedPassword}
            InputProps={{ readOnly: true }}
            variant="outlined"
            size="small"
            sx={{ fontFamily: "monospace" }}
          />
          <Button
            variant="outlined"
            onClick={handleCopy}
            disabled={!generatedPassword || loading}
            sx={{ flexShrink: 0, minWidth: 72 }}
          >
            Copy
          </Button>
        </div>
      </div>

      <p
        style={{
          fontSize: 13,
          color: "var(--cms-warning, #d97706)",
          margin: "8px 0 0",
        }}
      >
        The old password stays active until you confirm. This password will not
        be shown again.
      </p>

      <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || !generatedPassword}
        >
          Confirm
        </Button>
        <Button variant="outlined" onClick={closeDialog} disabled={loading}>
          Cancel
        </Button>
      </div>
    </div>
  )

  return [openDialog, dialogContent, loading, closeDialog]
}

export default McpResetPasswordDialog
